import { takeEvery, put, takeLatest } from 'redux-saga/effects';
import {
  orderInspection, updateBidInPrism, syncPurchaseState, runStatusOverrideSync, refreshTxnInspection,
  setChannelWorkflowLoadingOverlayState, appNotify, setIsSyncingPurchaseState, setErrorListModalState
} from 'store/actions';
import { postWithToken, postWithTokenV2 } from 'api/network';

export function* purchaseSaga() {
  yield takeEvery(orderInspection, function* ({ payload: txnOppIds }) {
    try {
      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: true, message: 'Ordering inspection...' }));

      yield postWithToken(`/api/underwrite/workflow/purchasing/orderInspection`, txnOppIds);
      yield put(appNotify('Order Requested', 'success'));

      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: false, message: null }));
    }
    catch (error) {
      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: false, message: null }));
      yield put(appNotify(error.message, 'error'));
    }
  });

  yield takeEvery(refreshTxnInspection, function* ({ payload: txnOppIds }) {
    try {
      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: true, message: 'Syncing inspection...' }));

      const driveByInspectionResponse = yield postWithToken(`/api/underwrite/workflow/inspection/syncInspection`, txnOppIds);
      const driveByInspectionResponseData = yield driveByInspectionResponse.json();

      let succeedCount = 0;
      let failCount = 0;

      driveByInspectionResponseData.forEach(driveByInspection => {
        // Keep track of succeed/fail count
        if (driveByInspection.syncSucceed) {
          succeedCount++;
        }
        else {
          failCount++;
        }
      });

      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: false, message: null }));

      if (succeedCount > 0) {
        yield put(appNotify(`${succeedCount} ${succeedCount > 1 ? 'inspections' : 'inspection'} updated!`, 'success'));
      }
      else if (succeedCount === 0) {
        yield put(appNotify('No inspection updated', 'warning'));
      }

      if (failCount > 0) {
        yield put(appNotify(`Failed to sync ${failCount} ${failCount > 1 ? 'inspections' : 'inspection'}. Please check inspection message.`, 'error'));
      }
    }
    catch (e) {
      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: false, message: null }));
      yield put(appNotify(`Fail to update inspection. ${e}`, 'error'));
    }
  });

  yield takeEvery(updateBidInPrism, function* ({ payload }) {
    const { txnOppIds, proformaUpdateObj } = payload;
    if (proformaUpdateObj) { // For submitting bid from proforma on underwrite page, for MLS/Off Market opps
      const { valuationId, userId, highestAndBest, overrideOrderValidation, callback } = proformaUpdateObj;
      if (valuationId == null || userId == null) {
        console.log('valuationId or userId missing', proformaUpdateObj);
        return;
      }
      try {
        yield postWithTokenV2(`/api/underwrite/bidSubmission/${txnOppIds}/updateBidSingle?valuationId=${valuationId}${highestAndBest != null ? `&highestAndBest=${highestAndBest}` : ''}&overrideOrderValidation=${overrideOrderValidation}`, txnOppIds);
        yield put(appNotify('Bid submission is being processed', 'success'));
      } catch (error) {
        var { details } = error

        if (details.length === 1)
        {
          var [firstDetails] = details
          yield put(appNotify(firstDetails, 'error'));
          callback && callback();
          return
        }

        yield put(setErrorListModalState({ header: 'Unable to submit bid for the following reasons', details }))
        const options = {
          showDetailsButton: true
        }
        yield put(appNotify('Unable to submit bid', 'error', options));
      }
      callback && callback();
      return;
    }
    try {
      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: true, message: 'Updating bid...' }));

      yield postWithTokenV2(`/api/underwrite/bidSubmission/updateBidBatch`, txnOppIds);
      const submissionsTxt = txnOppIds.length === 1 ? 'submission is' : 'submissions are'
      yield put(appNotify(`Bid ${submissionsTxt} being processed`, 'success'));

      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: false, message: null }));

    } catch (error) {
      const detailsBatch = error.details;
      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: false, message: null }));
      if (detailsBatch.length === 1) {
        var [firstDetailsBatch] = detailsBatch;
        yield put(appNotify(firstDetailsBatch, 'error'));
        return;
      }
      yield put(setErrorListModalState({ header: 'Unable to submit bid for the following reasons', details: detailsBatch }))
      const options = {
        showDetailsButton: true
      }
      yield put(appNotify('Unable to submit bid', 'error', options));
    }
  });

  yield takeLatest(syncPurchaseState, function* ({ payload: txnOppIds }) {
    try {
      //NOTE: rename PRISM to CRM in all methods cleanup
      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: true, message: 'Syncing purchasing state...' }));
      yield put(setIsSyncingPurchaseState(true));
      const response = yield postWithToken(`/api/underwrite/workflow/purchasing/syncState`, txnOppIds);
      const syncedTxnOppIds = yield response.json();
      if (syncedTxnOppIds.length > 0) {
        if (syncedTxnOppIds.length !== txnOppIds.length) {
          const notFoundOppIds = txnOppIds.filter(x => syncedTxnOppIds.indexOf(x) === -1);
          yield put(appNotify(`Synced ${syncedTxnOppIds.length} purchasing ${syncedTxnOppIds.length > 1 ? 'states' : 'state'} out of ${txnOppIds.length} requested transaction opportunities. Unable to sync ids: ${notFoundOppIds}`, 'warning'))
        } else {
          yield put(appNotify(`Synced ${syncedTxnOppIds.length} purchasing ${syncedTxnOppIds.length > 1 ? 'states' : 'state'} out of ${txnOppIds.length} requested transaction opportunities`, 'success'))
        }
      }
      else {
        yield put(appNotify(`No purchasing state found for the requested ${txnOppIds.length} transaction opportunities`, 'warning'));
      }
      yield put(setIsSyncingPurchaseState(false));
      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: false, message: null }));

    } catch (e) {
      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: false, message: null }));
      yield put(setIsSyncingPurchaseState(false));
      yield put(appNotify(`Sync purchasing state failed. ${e.message} `, 'error'));
    }
  });

  yield takeLatest(runStatusOverrideSync, function* ({ payload: txnOppIds }) {
    try {
      //NOTE: rename PRISM to CRM in all methods cleanup
      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: true, message: 'Running Status Override Sync...' }));
      yield put(setIsSyncingPurchaseState(true));
      const response = yield postWithToken(`/api/underwrite/workflow/purchasing/statusOverrideSync`, txnOppIds);
      const syncedTxnOppIdCount = yield response.json();
      if (syncedTxnOppIdCount > 0) {
        yield put(appNotify(`Successfully ran ${syncedTxnOppIdCount} status override sync for ${syncedTxnOppIdCount > 1 ? 'opportunities' : 'opportunity'} out of ${txnOppIds.length} requested.`, 'success'))
      }
      else {
        yield put(appNotify(`No purchasing state found for the requested ${txnOppIds.length} transaction opportunities`, 'warning'));
      }
      yield put(setIsSyncingPurchaseState(false));
      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: false, message: null }));

    } catch (e) {
      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: false, message: null }));
      yield put(setIsSyncingPurchaseState(false));
      yield put(appNotify(`Status override sync failed. ${e.message} `, 'error'));
    }
  })
}